import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * The last thing between one component throwing and the whole console going blank.
 *
 * 🔴 REACT UNMOUNTS THE ENTIRE TREE ON AN UNCAUGHT RENDER ERROR, and there was nothing here
 * to stop it. [X] Measured 2026-09-07: `/projects/intents` sent `rows` under another name,
 * one panel read `undefined.length`, and a teacher got a **white screen** with the failure
 * only in the browser console. The bug was small; the blast radius was the product.
 *
 * ⚠️ IT SHOWS THE MESSAGE, DELIBERATELY. The people who use this are the people who can
 * report it, and "something went wrong" gives them nothing to send. It is our own error text
 * — not a user's data — and it is already in their devtools.
 *
 * ⚠️ RESET ON NAVIGATION, VIA `resetKey`. A boundary that latches turns one broken page into
 * a session that stays broken until a reload, which reads as a much worse fault than it is.
 * The shell passes the pathname, so walking away from a bad page is the way out.
 */
export class ErrorBoundary extends Component<
  { resetKey?: string; children: ReactNode },
  { err: Error | null }
> {
  state: { err: Error | null } = { err: null };

  static getDerivedStateFromError(err: Error) {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // The console is where a person can copy it from, and it carries the component stack the
    // rendered message deliberately does not.
    console.error("a page failed to render", err, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey?: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.err) {
      this.setState({ err: null });
    }
  }

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="card">
        <div className="card-body">
          <p className="alert refused" role="alert">
            ⛔ This page could not be drawn. Nothing was changed by it.
          </p>
          <p className="small muted">{this.state.err.message}</p>
          <p className="small muted">
            Moving to another page clears this. If it keeps happening, the message above is
            the useful half to report.
          </p>
          <button className="btn" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
}
